import React from 'react';
import { Phone, Mail, MapPin, Navigation, Globe } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { Seo } from '@/components/Seo';
import { PageHero } from '@/components/PageHero';
import { SectionHeading } from '@/components/SectionHeading';
import { EnquiryForm } from '@/components/EnquiryForm';
import { Reveal } from '@/components/Reveal';

export default function Contact() {
  const { site } = useSite();

  const jsonLd = site && {
    '@context': 'https://schema.org', '@type': 'LocalBusiness',
    name: 'Sri Lakshmi Enterprises', image: 'https://www.sle.enterprises/media/og-image.png',
    telephone: site.phones, email: site.email,
    address: { '@type': 'PostalAddress', streetAddress: site.headOffice?.address, addressRegion: 'Tamil Nadu', addressCountry: 'IN' },
  };

  return (
    <>
      <Seo title='Contact Us' description="Let's move your project forward. Contact Sri Lakshmi Enterprises for excavation, demolition, blasting, dewatering and machinery hiring." path='/contact' image='/media/img/night-sparks.jpg' jsonLd={jsonLd} />
      <PageHero eyebrow='Contact' title="Let's move your project forward." subtitle='Tell us about your site and requirement. Our team will get back to you shortly.' image='/media/img/night-sparks.jpg' minH='min-h-[52vh]' />

      <section className='bg-[#F6F5F1] py-20 lg:py-28'>
        <div className='container-x grid gap-12 lg:grid-cols-12 lg:gap-16'>
          <div className='lg:col-span-5'>
            <SectionHeading eyebrow='Get in Touch' title='Talk to our team.' />
            <div className='mt-8 space-y-5'>
              {site?.phones?.map((p) => (
                <a key={p} href={`tel:${p.replace(/\s/g, '')}`} className='flex items-center gap-3 text-[#20272A]/80 hover:text-[#075B61]'>
                  <span className='flex h-10 w-10 items-center justify-center rounded-lg bg-[#075B61]/8 text-[#075B61]'><Phone size={17} /></span>
                  {p}
                </a>
              ))}
              {site?.email && (
                <a href={`mailto:${site.email}`} className='flex items-center gap-3 text-[#20272A]/80 hover:text-[#075B61]'>
                  <span className='flex h-10 w-10 items-center justify-center rounded-lg bg-[#075B61]/8 text-[#075B61]'><Mail size={17} /></span>
                  {site.email}
                </a>
              )}
              {site?.website && (
                <a href={`https://${site.website}`} target='_blank' rel='noreferrer' className='flex items-center gap-3 text-[#20272A]/80 hover:text-[#075B61]'>
                  <span className='flex h-10 w-10 items-center justify-center rounded-lg bg-[#075B61]/8 text-[#075B61]'><Globe size={17} /></span>
                  {site.website}
                </a>
              )}
            </div>

            <div className='mt-10 grid gap-5'>
              {(site?.offices || []).map((o) => (
                <Reveal key={o.label + o.city}>
                  <div className='rounded-2xl border border-[#20272A]/10 bg-white p-6'>
                    <span className='eyebrow text-[#075B61]'>{o.label} — {o.city}</span>
                    <p className='mt-3 flex items-start gap-2 text-sm text-[#20272A]/75'><MapPin size={16} className='mt-0.5 shrink-0 text-[#B88A3B]' /> {o.address}</p>
                    <a href={`https://www.google.com/maps/search/${encodeURIComponent(o.address)}`} target='_blank' rel='noreferrer' className='mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#075B61] hover:text-[#B88A3B]'>
                      <Navigation size={15} /> Get Directions
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className='lg:col-span-7'>
            <SectionHeading eyebrow='Project Enquiry' title='Request a project discussion.' />
            <div className='mt-8'>
              <EnquiryForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
